import PropTypes from 'prop-types'
import { useEffect, useContext, useState } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'

// Internal components
import LayoutOverview from '../../components/LayoutOverview/LayoutOverview'
import OverviewFilter from '../../components/OverviewFilter/OverviewFilter'
import OverviewGrid from '../../components/OverviewGrid/OverviewGrid'
import OverviewHeader from '../../components/OverviewHeader/OverviewHeader'

// Context
import { FilterContext } from '../../context/FilterContext'

// Utils
import { setLocalStorage, getLocalStorage } from '../../utils/store/localstorage'
import getOverviewFilterData from '../../utils/data/getOverviewFilterData'
import getResults from '../../utils/search/getResults'
import getSession from '../../utils/auth/getSession'
import getSiteHandle from '../../utils/i18n/getSiteHandle'
import queryServerSideData from '../../utils/data/queryServerSideData'
import { UserContext } from '../../context/UserContext'

// Environment consts
const searchApiLimit = 8
const searchApiUrl = process.env.NEXT_PUBLIC_ELASTICSEARCH_API
const selectedFilterStorageKey = 'selectedFilterStorage'

// Variable definitions
const OverviewPage = (props) => {
  const { type, filters, search, site } = props
  if (!type || type === undefined) return false

  // Context
  const filterContext = useContext(FilterContext)
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // State
  const [resultState, setResultState] = useState(false)
  const [typeState, setTypeState] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [overviewFilterDataState, setOverviewFilterDataState] = useState([])
  const [selectedFilterStorageState, setSelectedFilterStorageState] = useState([])
  const [searchUrlState, setSearchUrlState] = useState(false)
  const [constructorHasRun, setConstructorHasRun] = useState(false)

  // Load overview on mount
  useEffect(() => {
    if (constructorHasRun) return
    setTypeState(type)
    updateContextFromStorage()
    runOverviewConstructor()
  }, [])

  // Update overview when navigating between overview types
  useEffect(() => {
    if (typeState !== type) setResultState(false)
    setTypeState(type)
    runOverviewConstructor()
    if (search?.pageInfo?.limit === filterContext.from) {
      startSearch()
    } else {
      filterContext.setFrom(search?.pageInfo?.limit)
    }
  }, [type])

  useEffect(() => {
    setIsLoading(true)
    updateStorageFromContext()
  }, [filterContext.selectedFilters])

  useEffect(() => {
    if (!selectedFilterStorageState) return false
    if (
      Object.keys(selectedFilterStorageState).length !== 0 &&
      JSON.stringify(selectedFilterStorageState) === JSON.stringify(filterContext.selectedFilters)
    ) {
      if (search?.pageInfo?.limit === filterContext.from) {
        startSearch(true)
      } else {
        filterContext.setFrom(search?.pageInfo?.limit)
      }
    }
  }, [selectedFilterStorageState])

  // Append new search result when load more is pressed
  useEffect(() => {
    startSearch()
  }, [filterContext.from])

  // Leave for debugging
  // useEffect(() => {
  //   console.log('searchUrlState:', searchUrlState)
  // }, [searchUrlState])

  // useEffect(() => {
  //   console.log('resultState', resultState)
  // }, [resultState])

  const runOverviewConstructor = () => {
    setIsLoading(true)
    if (search?.aggregations) {
      const overviewFilterData = getOverviewFilterData(filters, search.aggregations, t)
      setOverviewFilterDataState(overviewFilterData)
    }
    if (search?.pageInfo) {
      filterContext.setFrom(search?.pageInfo?.limit)
      filterContext.setTotal(search?.pageInfo?.total)
    }
    setConstructorHasRun(true)
  }

  const updateStorageFromContext = () => {
    if (
      Object.keys(filterContext.selectedFilters).length !== 0 &&
      JSON.stringify(selectedFilterStorageState[type]) !== JSON.stringify(filterContext.selectedFilters[type])
    ) {
      setLocalStorage(selectedFilterStorageKey, filterContext.selectedFilters)
      setSelectedFilterStorageState(filterContext.selectedFilters)
    } else {
      setResultState(false)
      startSearch()
    }
  }

  const updateContextFromStorage = () => {
    const localStorage = getLocalStorage(selectedFilterStorageKey)
    const tempFilterValues = { ...filterContext.selectedFilters }
    for (const filterType in localStorage) {
      if (localStorage[filterType] === filterContext.selectedFilters[filterType]) return
      tempFilterValues[filterType] = localStorage[filterType]
    }
    filterContext.setSelectedFilters(tempFilterValues)
  }

  const startSearch = async () => {
    if (!selectedFilterStorageState[type]) return
    const searchsUrl = getSearchUrl()
    if (searchsUrl === searchUrlState) return
    setSearchUrlState(searchsUrl)
    const searchResponse = await fetch(`/api/knvb/search?url=${encodeURIComponent(searchsUrl)}&site=${site}`)
    const searchResults = await searchResponse.json()
    if (JSON.stringify(searchResults?.results) === JSON.stringify(resultState)) {
      setIsLoading(false)
      return
    }
    const overviewFilterData = getOverviewFilterData(filters, searchResults.aggregations, t)
    if (overviewFilterData !== overviewFilterDataState) setOverviewFilterDataState(overviewFilterData)
    if (filterContext.total !== searchResults?.pageInfo?.total) filterContext.setTotal(searchResults?.pageInfo?.total)
    setResultState(searchResults?.results ? [...searchResults.results] : [])
    setIsLoading(false)
  }

  const getSearchUrl = () => {
    const searchParams = new URLSearchParams()
    const activeFilters = selectedFilterStorageState[type]
    Object.keys(activeFilters).forEach((key) => {
      if (activeFilters[key]) {
        if (!key.includes('UserGenerated')) searchParams.append(`${key}[]`, activeFilters[key])
        if (key.includes('UserGenerated') && activeFilters[key])
          searchParams.append('userGenerated', activeFilters[key])
      }
    })
    searchParams.append('from', filterContext.from - searchApiLimit)
    searchParams.append('limit', searchApiLimit)
    searchParams.append('site', site)

    const searchResultsQuery = searchParams.toString().length ? `?${searchParams.toString()}` : ''
    const searchResultsUrl = `${searchApiUrl}/${type}/search${searchResultsQuery}`
    return searchResultsUrl
  }

  return (
    <>
      <LayoutOverview
        type={type}
        headerContent={<OverviewHeader data={filters.data} type={type} />}
        filterContent={overviewFilterDataState && <OverviewFilter data={overviewFilterDataState} type={type} />}
        mainContent={resultState && <OverviewGrid data={resultState} loading={isLoading} type={type} />}
      />
    </>
  )
}

export async function getServerSideProps({ params, req, res, locale }) {
  const session = await getSession(req, res)

  if (session.error) {
    return {
      props: {
        error: true,
        errorCode: session.status,
        message: session.message,
        ...(await serverSideTranslations(
          locale || process.env.NEXT_PUBLIC_RINUS_DEFAULT_LANGUAGE,
          process.env.NEXT_PUBLIC_RINUS_NAMESPACES?.split(',')
        ))
      }
    }
  }

  const siteHandle = getSiteHandle(locale)

  if (['exercises', 'trainings', 'programs'].indexOf(params.type) <= -1) {
    return {
      notFound: true
    }
  }

  // globals
  const globalJson = await queryServerSideData(
    'qGlobal.graphql',
    { site: siteHandle, seoUri: `/overview/${params.type}`, seoSite: siteHandle },
    session.cookieString
  )

  // initial search results
  const searchUrl = `${searchApiUrl}/${params.type}/search?site=${siteHandle}&limit=${searchApiLimit}`
  const searchJson = await getResults(req, res, searchUrl)

  let query = ''
  switch (params.type) {
    case 'exercises':
      query = 'qExerciseOverview.graphql'
      break
    case 'trainings':
      query = 'qTrainingOverview.graphql'
      break
    case 'programs':
      query = 'qProgramOverview.graphql'
  }

  const filtersJson = await queryServerSideData(query, { site: siteHandle }, session.cookieString)

  return {
    props: {
      site: siteHandle,
      ...(await serverSideTranslations(
        locale || process.env.NEXT_PUBLIC_RINUS_DEFAULT_LANGUAGE,
        process.env.NEXT_PUBLIC_RINUS_NAMESPACES?.split(',')
      )),
      ...globalJson.data,
      type: params.type,
      search: { ...searchJson },
      filters: { ...filtersJson }
    }
  }
}

OverviewPage.propTypes = {
  site: PropTypes.string,
  type: PropTypes.string,
  filters: PropTypes.object,
  search: PropTypes.object
}

export default OverviewPage
